<template>
    <slot
        name="clickTrigger"
        :triggerFilePicker
    />

    <div
        class="ds-upload flex flex-col transition-[height] duration-150 ease-in-out"
        :class="{ 'h-full': size === 'full', disabled, hidden: size === 'collapsed' }"
    >
        <div
            ref="dropzoneRef"
            class="ds-upload-dropzone transition-[height,border,background-color,opacity,padding] duration-150 ease-in-out"
            :class="{
                [`ds-upload-size-${props.size}`]: true,
                'ds-upload-drag-active': isOverDropZone,
                'ds-upload-error': error,
                'cursor-not-allowed': disabled,
            }"
            role="button"
            tabindex="0"
            @click="triggerFilePicker"
            @keydown.enter.prevent="triggerFilePicker"
            @keydown.space.prevent="triggerFilePicker"
        >
            <DsSpinner
                v-if="isLoading"
                size="xl"
                variant="secondary"
            />
            <div
                v-else
                class="ds-upload-content"
            >
                <div class="icon-wrapper">
                    <DsIcon
                        :icon="isOverDropZone ? 'fa-regular fa-image' : 'fa-regular fa-cloud-arrow-up'"
                        size="xl"
                    />
                </div>

                <slot name="dropzone-text">
                    <div class="text">
                        {{ dropzoneText }}
                    </div>
                </slot>

                <div
                    v-if="!isOverDropZone"
                    class="or-text"
                >
                    or
                </div>

                <DsButton
                    v-if="!isOverDropZone"
                    class="mt-2"
                    :disabled
                    size="sm"
                    variant="secondary"
                    @click.stop="triggerFilePicker"
                >
                    <slot name="button-text">Select documents</slot>
                </DsButton>
            </div>
        </div>

        <ErrorMessage
            v-if="error"
            id="upload-error"
            :message="errorMessage"
        />
        <div
            v-else-if="helperText"
            id="upload-helper"
            class="ds-upload-hint ds-upload-helper"
        >
            {{ helperText }}
        </div>
        <input
            ref="fileInputRef"
            :accept="acceptString"
            aria-hidden="true"
            class="sr-only"
            :multiple
            type="file"
            @change="onFileInputChange"
        />
    </div>
</template>

<script setup lang="ts">
import type { DocumentDataType } from '@/components/design-system/upload/document-data-types';
import { DocumentDataTypeExtension } from '@/components/design-system/upload/document-data-types';
import type { DsUploadClickTriggerSlotProps } from '@/components/design-system/upload/ds-upload.types';
import {
    DsUploadEmitErrorReason,
    type DsUploadProps,
    type DsUploadTrigger,
    type StagedDocument,
} from '@/components/design-system/upload/ds-upload.types';
import { formatFileSize } from '@/shared/utils/file-size-convert';
import DsIcon from '@ds/DsIcon.vue';
import DsButton from '@ds/button/DsButton.vue';
import DsSpinner from '@ds/spinner/DsSpinner.vue';
import ErrorMessage from '@ds/utils/ErrorMessage.vue';
import { useDropZone } from '@vueuse/core';
import { v4 as uuidV4 } from 'uuid';
import { computed, nextTick, useTemplateRef } from 'vue';

const generateDocumentId = (): string => {
    return uuidV4();
};

const fileList = defineModel<StagedDocument[]>('fileList', { default: [] });
const error = defineModel<string | boolean>('error', { default: false });

const props = withDefaults(defineProps<DsUploadProps>(), {
    isLoading: false,
    multiple: true,
    dataTypes: () => [],
    size: 'lg',
    maxFileSize: undefined,
    required: false,
});

const emit = defineEmits<{
    'file-uploaded': [payload: { file: File }];
    'file-upload-error': [payload: { file: File; reason: DsUploadEmitErrorReason }];
    'upload-triggered': [DsUploadTrigger];
}>();

defineSlots<{
    clickTrigger: DsUploadClickTriggerSlotProps;
    'dropzone-text': () => void;
    'button-text': () => void;
}>();

const dropzoneRef = useTemplateRef('dropzoneRef');
const fileInputRef = useTemplateRef('fileInputRef');

const errorMessage = computed<string>(() => {
    const defaultMessage = props.multiple ? 'Add one or more documents' : 'Add a document';

    if (error.value === true) return defaultMessage; // avoid rendering the word `true`

    return error.value || defaultMessage;
});

const dropzoneText = computed(() => {
    if (isOverDropZone.value) {
        return 'Drop documents to upload';
    }
    return 'Drag and drop a document to upload';
});

const acceptString = computed(() => {
    return props.dataTypes.length > 0 ? props.dataTypes.join(',') : '';
});

const helperText = computed(() => {
    if (props.dataTypes.length === 0) {
        return '';
    }

    const extensions = props.dataTypes
        .map(type => {
            const ext = DocumentDataTypeExtension[type];
            return ext ? ext.slice(1).toUpperCase() : '';
        })
        .filter(Boolean)
        .join(', ');

    return `Accepted document types: ${extensions}`;
});

const validateFile = (file: File): boolean => {
    if (props.dataTypes.length > 0 && !props.dataTypes.includes(file.type as DocumentDataType)) {
        emit('file-upload-error', { file, reason: DsUploadEmitErrorReason.INVALID_TYPE });
        error.value = `File type not accepted: ${file.name}`;
        return false;
    }

    if (props.maxFileSize && file.size > props.maxFileSize) {
        emit('file-upload-error', { file, reason: DsUploadEmitErrorReason.SIZE_EXCEEDED });
        const sizeLimit = formatFileSize(props.maxFileSize);
        error.value = `File size exceeds the ${sizeLimit} limit. Try another file.`;
        return false;
    }

    emit('file-uploaded', { file });
    return true;
};

const validateRequired = () => {
    if (props.required && fileList.value.length === 0) {
        error.value = 'Add one or more documents';
        return;
    }
    error.value = '';
};

const processFiles = (files: File[] | FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const validFiles: StagedDocument[] = [];

    for (const file of fileArray) {
        if (validateFile(file)) {
            validFiles.push({
                id: generateDocumentId(),
                fileName: file.name,
                file,
            });
        }
    }

    if (validFiles.length === 0) {
        return;
    }
    if (props.multiple) {
        fileList.value = [...fileList.value, ...validFiles];
    } else {
        fileList.value = [validFiles[0]];
    }
    nextTick(validateRequired);
};

const onFileInputChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.files) {
        processFiles(input.files);
        emit('upload-triggered', 'file-input');
        input.value = '';
    }
};

const triggerFilePicker = () => {
    fileInputRef.value?.click();
};

const { isOverDropZone } = useDropZone(dropzoneRef, {
    onDrop: files => {
        processFiles(files);
        emit('upload-triggered', 'dropzone');
    },
    multiple: props.multiple,
});
</script>

<style scoped lang="scss">
.ds-upload {
    @apply grid gap-1;

    .ds-upload-dropzone {
        @apply grid items-center justify-center gap-2 font-normal;
        @apply cursor-pointer rounded-lg border border-dashed;
        @apply px-6 py-6 transition-colors duration-200;
        @apply border-ds-primary-400 bg-secondary;

        .ds-upload-content {
            @apply mx-auto flex flex-col justify-center space-y-1 font-normal text-content-primary;

            & > * {
                @apply mx-auto;
            }

            .icon-wrapper {
                @apply flex items-center justify-center rounded-full;
            }

            .text {
                @apply text-center text-content-lg font-medium text-content-primary;
            }

            .or-text {
                @apply text-content-md font-normal text-content-secondary;
            }
        }

        &:focus-visible {
            @apply outline-none ring-4 ring-ds-gray-300;
        }

        &.ds-upload-size-full {
            @apply h-full;
        }

        &.ds-upload-size-lg {
            @apply h-[600px];
        }

        &.ds-upload-size-md {
            @apply h-[182px];
        }

        &.ds-upload-error {
            @apply border-2 border-ds-red-600 bg-ds-red-50;
        }

        &.ds-upload-drag-active {
            @apply border-2 border-semantic-primary-default bg-ds-gray-200;

            .ds-upload-content {
                .icon-wrapper {
                    @apply h-16 w-16 bg-ds-gray-200 p-3 text-semantic-primary-default;
                }
                .hide-active {
                    @apply opacity-0;
                }
            }
        }
    }

    .ds-upload-hint {
        @apply flex items-center gap-1 text-content-md;
    }
    .ds-upload-error {
        @apply text-content-error;
    }

    .ds-upload-helper {
        @apply text-content-sm text-content-secondary;
    }

    &.disabled {
        @apply opacity-50;
    }
}
</style>
